import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SuiteIcon } from '@shared/icons';
import { useMobileTabs, MobileTabItem } from './mobileTabs';
import { useMobileShell } from '@shared/shell';
import { useKeyboardOpen } from '@shared/hooks/useKeyboardOpen';
import { triggerHaptic } from '@shared/lib/haptics';

export const MobileBottomNav: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const tabs = useMobileTabs();
  const { launcherOpen, toggleLauncher, closeLauncher } = useMobileShell();
  const isKeyboardOpen = useKeyboardOpen();

  if (isKeyboardOpen) {
    return null;
  }

  const isTabActive = (tab: MobileTabItem) => {
    if (tab.isMore) {
      return launcherOpen;
    }
    if (launcherOpen) return false;
    if (location.pathname === tab.path) return true;
    if (tab.path !== '/' && location.pathname.startsWith(tab.path)) return true;
    return false;
  };

  const handleTabClick = (tab: MobileTabItem) => {
    triggerHaptic('light');
    if (tab.isMore) {
      toggleLauncher();
    } else {
      closeLauncher();
      navigate(tab.path);
    }
  };

  return (
    <nav
      aria-label="Mobile Bottom Navigation"
      data-mobile-bottom-nav
      className="md:hidden fixed inset-x-0 bottom-0 z-40 bg-card/95 backdrop-blur-md border-t border-border/60 pb-[var(--safe-bottom)] select-none isolate"
    >
      <div
        className="grid h-14 short:h-11 items-center"
        style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}
      >
        {tabs.map((tab) => {
          const active = isTabActive(tab);
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => handleTabClick(tab)}
              aria-current={active ? 'page' : undefined}
              className={`flex flex-col items-center justify-center h-full w-full py-1 short:py-0.5 text-[11px] short:text-[9px] transition-colors ${
                active
                  ? 'text-primary font-semibold'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <div className="relative">
                <SuiteIcon
                  name={tab.icon}
                  className={`h-5 w-5 short:h-4 short:w-4 transition-transform ${
                    active ? 'scale-110 short:scale-100' : ''
                  }`}
                />
              </div>
              <span className="truncate max-w-[64px] mt-0.5 tracking-tight leading-tight">
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default MobileBottomNav;
